import axios from 'axios';
import envConfig from 'commons-vehicle/config/index.js';

import { List } from 'immutable';
import { isArrayLikeObject, isEmpty, isNull, isString, map, find, isUndefined, flatten, filter } from 'lodash';
import { LocaleService, ConfigsService, HTTPService } from '@sm360/widget-commons-core-services';

import ShowroomAPIService from 'commons-vehicle/services/ShowroomService/ShowroomAPIService.js';

import DISCLAIMERS from 'commons-vehicle/PurchaseMethods/constants/disclaimers.js';
import PURCHASE_METHODS from 'commons-vehicle/PurchaseMethods/constants/purchaseMethodsValues.js';
import { VEHICLE } from 'commons-vehicle/constants/vehicle.js';

import VehicleModel from 'commons-vehicle/Vehicle/models/VehicleModel.js';
import ModelYearModel from 'commons-vehicle/ModelYear/models/ModelYearModel.js';
import DisclaimersModel from 'commons-vehicle/PurchaseMethods/models/DisclaimersModel.js';

/**
 * Util to create a request configuration object to load Generic Disclaimers for a given context
 * @param {String} publicToken
 */
function getGenericDisclaimersRequestConfig(publicToken, config) {
    const orgId = config?.orgId || ConfigsService.getOrganizationId();
    const orgUnitId = config?.orgUnitId || ConfigsService.getOrganizationUnitId();
    const location = config?.location || ConfigsService.getOrganizationUnitLocation();
    const language = LocaleService.getLocale();

    const requestConfig = {
        url: `${envConfig.endpoint.showroom}/service/v1/genericDisclaimers`,
        method: 'get',
    };

    requestConfig.headers = {
        'x-jwt': isString(publicToken) ? publicToken : '',
    };

    requestConfig.params = {
        context: `${isNull(orgId) ? '' : orgId}.${isNull(orgUnitId) ? '' : orgUnitId}`,
        location: isEmpty(location) ? '' : location,
        language,
    };

    return requestConfig;
}

/**
 * Util to create a request configuration data and vehicle match object
 * @param {Object[]} vehicles
 */
function getVehiclesDisclaimersDataRequestConfig(vehicles) {
    const vehiclesAndRequestData = map(vehicles, (vehicle, index) => {
        const {
            year,
            make,
            model,
            vehicleConditionType,
            modelTrimCode,
            configurationID,
            configurationToken,
            vehicleId,
            odometer,
            sellingPrice,
            purchaseMethods,
            showroomVehicleId,
        } = vehicle;

        const { tradeIn, lien } = purchaseMethods;

        const disclaimers = purchaseMethods.getDisclaimerParameters();

        let disclaimerData;
        if (vehicle.getIsVehicleNew()) {
            disclaimerData = {
                cookie: index.toString(),
                year,
                make,
                modelTrimCode,
                configurationID,
                configurationToken,
                vehicleCondition: vehicleConditionType,
                vehicleId,
                showroomVehicleId,
                tradeIn,
                lien,
                disclaimers,
            };
        } else {
            disclaimerData = {
                cookie: index.toString(),
                year,
                make,
                model,
                vehicleCondition: vehicleConditionType,
                vehicleId,
                showroomVehicleId,
                odometer,
                retail: sellingPrice,
                tradeIn,
                lien,
                disclaimers,
            };
        }

        return {
            cookie: index.toString(),
            disclaimerData,
            vehicle,
        };
    });

    return vehiclesAndRequestData;
}

/**
 * Util to create a request configuration data and options package match object
 * @param {Object} modelYear
 * @param {Object} optionsPackage
 */
function getOptionsPackageDisclaimersDataRequestConfig(modelYear, optionsPackage) {
    const { year, make } = modelYear;

    const { modelTrimCode, id, configurationToken, purchaseMethods } = optionsPackage.configuration;

    const cookie = `${year}-${make}-${modelTrimCode}-${id}`;

    const { tradeIn, lien } = purchaseMethods;

    const disclaimers = purchaseMethods.getDisclaimerParameters();

    const disclaimerData = {
        cookie,
        year,
        make,
        modelTrimCode,
        configurationID: id,
        configurationToken,
        vehicleCondition: VEHICLE.CONDITIONS.NEW,
        tradeIn,
        lien,
        disclaimers,
    };

    return [
        {
            cookie,
            disclaimerData,
            modelYear,
            optionsPackage,
        },
    ];
}

/**
 * Util to create a request configuration data and model match object
 * @param {Object} model
 */
function getModelsDisclaimersDataRequestConfig(models) {
    const modelsAndRequestData = map(models, (model) => {
        const {
            priceYear,
            leaseYear,
            financeYear,
            make,
            bodyStyleCode,
            priceModelTrimCode,
            leaseModelTrimCode,
            financeModelTrimCode,
            priceConfigurationId,
            leaseConfigurationId,
            financeConfigurationId,
            purchaseMethods,
        } = model;
        if (!purchaseMethods) {
            return [];
        }

        const cookie = `${make}-${model.model}-${bodyStyleCode}`;

        const { tradeIn, lien } = purchaseMethods;

        const disclaimers = purchaseMethods.getDisclaimerParameters();

        const priceDisclaimerData = {
            cookie: `${cookie}-price`,
            year: priceYear,
            make,
            modelTrimCode: priceModelTrimCode,
            configurationID: priceConfigurationId,
            vehicleCondition: VEHICLE.CONDITIONS.NEW,
            tradeIn,
            lien,
            disclaimers,
        };

        const leaseDisclaimerData = {
            cookie: `${cookie}-lease`,
            year: leaseYear,
            make,
            modelTrimCode: leaseModelTrimCode,
            configurationID: leaseConfigurationId,
            vehicleCondition: VEHICLE.CONDITIONS.NEW,
            tradeIn,
            lien,
            disclaimers,
        };

        const financeDisclaimerData = {
            cookie: `${cookie}-finance`,
            year: financeYear,
            make,
            modelTrimCode: financeModelTrimCode,
            configurationID: financeConfigurationId,
            vehicleCondition: VEHICLE.CONDITIONS.NEW,
            tradeIn,
            lien,
            disclaimers,
        };

        const { CASH, FINANCE, LEASE } = PURCHASE_METHODS;
        return [
            {
                cookie: `${cookie}-price`,
                disclaimerData: priceDisclaimerData,
                model,
                purchaseMethod: CASH,
                purchaseMethods,
            },
            {
                cookie: `${cookie}-lease`,
                disclaimerData: leaseDisclaimerData,
                model,
                purchaseMethod: LEASE,
                purchaseMethods,
            },
            {
                cookie: `${cookie}-finance`,
                disclaimerData: financeDisclaimerData,
                model,
                purchaseMethod: FINANCE,
                purchaseMethods,
            },
        ];
    });

    return flatten(modelsAndRequestData);
}

/**
 * Util to create a request configuration object to load Disclaimers for given data requests
 * @param {String} publicToken
 * @param {Object[]} requestsData
 */
function getDisclaimersRequestConfig(publicToken, requestsData) {
    const orgId = ConfigsService.getOrganizationId();
    const orgUnitId = ConfigsService.getOrganizationUnitId();
    const location = ConfigsService.getOrganizationUnitLocation();
    const language = LocaleService.getLocale();

    const requestConfig = {
        url: `${envConfig.endpoint.showroom}/service/v1/disclaimers`,
        method: 'post',
    };

    requestConfig.headers = {
        'x-jwt': isString(publicToken) ? publicToken : '',
    };

    requestConfig.params = {
        context: `${isNull(orgId) ? '' : orgId}.${isNull(orgUnitId) ? '' : orgUnitId}`,
        location: isEmpty(location) ? '' : location,
        language,
    };

    requestConfig.data = map(requestsData, (vehicle) => vehicle.disclaimerData);

    return requestConfig;
}

/**
 * Util to create a term frequency disclaimers sentences object
 * @param {Object} paymentFrequency
 * @param {Object} disclaimersIds
 */
function createTermFrequencyDisclaimersSentences(paymentFrequency, disclaimersIds) {
    const sentences = {
        paymentFrequency: 0,
        paymentAndCash: '',
        obligationAndInterest: '',
    };
    if (isEmpty(paymentFrequency) || isEmpty(paymentFrequency.disclaimers)) {
        return sentences;
    }
    sentences.paymentFrequency = paymentFrequency.frequency;

    const { PAYMENT_CASH_DOWN, TOTAL_OBLIGATION, COST_OF_CREDIT, CREDIT_RATE } = disclaimersIds;

    const paymentAndCash = find(paymentFrequency.disclaimers, (disclaimer) => disclaimer.id === PAYMENT_CASH_DOWN);
    sentences.paymentAndCash = paymentAndCash ? paymentAndCash.disclaimer : '';

    const totalObligation = find(paymentFrequency.disclaimers, (disclaimer) => disclaimer.id === TOTAL_OBLIGATION);
    const costOfCredit = find(paymentFrequency.disclaimers, (disclaimer) => disclaimer.id === COST_OF_CREDIT);
    const creditRate = find(paymentFrequency.disclaimers, (disclaimer) => disclaimer.id === CREDIT_RATE);
    sentences.obligationAndInterest =
        `${totalObligation ? totalObligation.disclaimer : ''} ` +
        `${costOfCredit ? costOfCredit.disclaimer : ''} ${creditRate ? creditRate.disclaimer : ''}`;

    return sentences;
}

/**
 * Util to create a term disclaimers sentences object
 * @param {Object} term
 * @param {String} priceExclusions
 * @param {Object} disclaimersIds
 */
function createTermDisclaimersSentences(term, priceExclusions, disclaimersIds) {
    const sentences = {
        term: 0,
        sellingPrice: '',
        priceInclusions: '',
        priceExclusions,
        termAndApr: '',
        frequencies: [],
    };
    if (isEmpty(term) || isEmpty(term.disclaimers)) {
        return sentences;
    }
    sentences.term = term.term;

    const { TERM, PAYMENT_FREQUENCY } = disclaimersIds;
    const { SELLING_PRICE, INCLUDED_REBATES, INCLUDED_FEES, EXCLUDED_FEES, TERM_APR } = TERM;

    const sellingPrice = find(term.disclaimers, (disclaimer) => disclaimer.id === SELLING_PRICE);
    sentences.sellingPrice = sellingPrice ? sellingPrice.disclaimer : '';

    const rebateInclusions = find(term.disclaimers, (disclaimer) => disclaimer.id === INCLUDED_REBATES);
    const feeInclusions = find(term.disclaimers, (disclaimer) => disclaimer.id === INCLUDED_FEES);
    const rebateInclusionsSentence = rebateInclusions ? rebateInclusions.disclaimer : '';
    const feeInclusionsSentence = feeInclusions ? feeInclusions.disclaimer : '';
    sentences.priceInclusions = `${rebateInclusionsSentence} ${feeInclusionsSentence}`;

    const feeExclusions = find(term.disclaimers, (disclaimer) => disclaimer.id === EXCLUDED_FEES);
    sentences.priceExclusions = `${feeExclusions ? feeExclusions.disclaimer : ''} ${priceExclusions}`;

    const termAndApr = find(term.disclaimers, (disclaimer) => disclaimer.id === TERM_APR);
    sentences.termAndApr = termAndApr ? termAndApr.disclaimer : '';

    sentences.frequencies = map(term.paymentFrequencies, (paymentFrequency) =>
        createTermFrequencyDisclaimersSentences(paymentFrequency, PAYMENT_FREQUENCY)
    );

    return sentences;
}

/**
 * Util to create a finance plan disclaimers sentences object
 * @param {Object} finance
 * @param {String} priceExclusions
 * @param {Object} disclaimersIds
 */
function createFinanceDisclaimersSentences(finance, priceExclusions, disclaimersIds) {
    const sentences = {
        vehicleDescription: '',
        terms: [],
        creditApproval: LocaleService.t('DISCLAIMERS_DEFAULT_VALUES.CREDIT_APPROVAL'),
    };
    if (isEmpty(finance)) {
        return sentences;
    }

    const {
        VEHICLE_DESCRIPTION,
        TERM,
        KM_PLAN,
        CASH_DOWN_TAX_EXCLUDED,
        CREDIT_APPROVAL,
        PAYMENT_FREQUENCY,
    } = disclaimersIds;

    const vehicleDescription = find(finance.disclaimers, (disclaimer) => disclaimer.id === VEHICLE_DESCRIPTION);
    sentences.vehicleDescription = vehicleDescription ? vehicleDescription.disclaimer : '';

    sentences.terms = map(finance.terms, (term) =>
        createTermDisclaimersSentences(term, priceExclusions, { TERM, PAYMENT_FREQUENCY })
    );

    const kmPlan = find(finance.disclaimers, (disclaimer) => disclaimer.id === KM_PLAN);
    const cashDownTaxExcluded = find(finance.disclaimers, (disclaimer) => disclaimer.id === CASH_DOWN_TAX_EXCLUDED);
    const creditApproval = find(finance.disclaimers, (disclaimer) => disclaimer.id === CREDIT_APPROVAL);
    sentences.creditApproval =
        `${kmPlan ? kmPlan.disclaimer : ''} ` +
        `${creditApproval ? creditApproval.disclaimer : ''} ` +
        `${cashDownTaxExcluded ? cashDownTaxExcluded.disclaimer : ''}`;

    return sentences;
}

/**
 * Util to create a cash purchase disclaimers sentences object
 * @param {Object} cashPurchase
 * @param {String} priceExclusions
 * @param {Object} disclaimersIds
 */
function createCashPurchaseDisclaimersSentences(cashPurchase, priceExclusions, disclaimersIds) {
    const sentences = {
        vehicleDescription: '',
        sellingPrice: '',
        priceInclusions: '',
        priceExclusions,
    };
    if (isEmpty(cashPurchase)) {
        return sentences;
    }

    const { VEHICLE_DESCRIPTION, SELLING_PRICE, INCLUDED_REBATES, INCLUDED_FEES, EXCLUDED_FEES } = disclaimersIds;

    const vehicleDescription = find(cashPurchase.disclaimers, (disclaimer) => disclaimer.id === VEHICLE_DESCRIPTION);
    sentences.vehicleDescription = vehicleDescription ? vehicleDescription.disclaimer : '';

    const sellingPrice = find(cashPurchase.disclaimers, (disclaimer) => disclaimer.id === SELLING_PRICE);
    sentences.sellingPrice = sellingPrice ? sellingPrice.disclaimer : '';

    const rebateInclusions = find(cashPurchase.disclaimers, (disclaimer) => disclaimer.id === INCLUDED_REBATES);
    const feeInclusions = find(cashPurchase.disclaimers, (disclaimer) => disclaimer.id === INCLUDED_FEES);
    const rebateInclusionsSentence = rebateInclusions ? rebateInclusions.disclaimer : '';
    const feeInclusionsSentence = feeInclusions ? feeInclusions.disclaimer : '';
    sentences.priceInclusions = `${rebateInclusionsSentence} ${feeInclusionsSentence}`;

    const feeExclusions = find(cashPurchase.disclaimers, (disclaimer) => disclaimer.id === EXCLUDED_FEES);
    sentences.priceExclusions = `${feeExclusions ? feeExclusions.disclaimer : ''} ${priceExclusions}`;

    return sentences;
}

/**
 * Util to create a purchase methods disclaimers sentences object
 * @param {Object} dataDisclaimers
 * @param {String} priceExclusions
 */
function createDisclaimersSentences(vehicleDisclaimers, priceExclusions) {
    if (isEmpty(vehicleDisclaimers)) {
        return null;
    }

    const { CASH, FINANCE, LEASE } = PURCHASE_METHODS;

    const cashPurchase = find(
        vehicleDisclaimers.purchaseMethods,
        (purchaseMethod) => purchaseMethod.purchaseMethod === CASH
    );

    const finance = find(
        vehicleDisclaimers.purchaseMethods,
        (purchaseMethod) => purchaseMethod.purchaseMethod === FINANCE
    );

    const lease = find(vehicleDisclaimers.purchaseMethods, (purchaseMethod) => purchaseMethod.purchaseMethod === LEASE);

    return {
        cookie: vehicleDisclaimers.cookie,
        cashPurchase: createCashPurchaseDisclaimersSentences(cashPurchase, priceExclusions, DISCLAIMERS.CASH),
        finance: createFinanceDisclaimersSentences(finance, priceExclusions, DISCLAIMERS.FINANCE),
        lease: createFinanceDisclaimersSentences(lease, priceExclusions, DISCLAIMERS.LEASE),
    };
}

/**
 * Util to get the term and frequency finance disclaimers sentences object
 * @param {Object} finance
 * @param {Object} financeDisclaimers
 */
function getFinanceSentences(finance, financeDisclaimers) {
    let financeSentences = {
        vehicleDescription: '',
        sellingPrice: '',
        priceInclusions: '',
        priceExclusions: '',
        termAndApr: '',
        paymentAndCash: '',
        obligationAndInterest: '',
        creditApproval: '',
    };

    const financeTerm = finance.getSelectedTerm();
    const financeFrequency = finance.getSelectedTermFrequency();
    if (!financeTerm || !financeTerm.term || !financeFrequency || !financeFrequency.paymentFrequency) {
        return financeSentences;
    }

    const financeTermDisclaimers = find(financeDisclaimers.terms, (term) => term.term === financeTerm.term);
    if (!financeTermDisclaimers) {
        return financeSentences;
    }

    const financeFrequencyDisclaimers = find(
        financeTermDisclaimers.frequencies,
        (frequency) => frequency.paymentFrequency === financeFrequency.paymentFrequency
    );
    if (!financeFrequencyDisclaimers) {
        return financeSentences;
    }

    financeSentences = {
        vehicleDescription: financeDisclaimers.vehicleDescription,
        sellingPrice: financeTermDisclaimers.sellingPrice,
        priceInclusions: financeTermDisclaimers.priceInclusions,
        priceExclusions: financeTermDisclaimers.priceExclusions,
        termAndApr: financeTermDisclaimers.termAndApr,
        paymentAndCash: financeFrequencyDisclaimers.paymentAndCash,
        obligationAndInterest: financeFrequencyDisclaimers.obligationAndInterest,
        creditApproval: financeDisclaimers.creditApproval,
    };

    return financeSentences;
}

/**
 * Util to get a basic disclaimers object
 * @param {Object} genericDisclaimers
 */
function getBasicDisclaimers(genericDisclaimers) {
    const sentences = {
        illustration: LocaleService.t('DISCLAIMERS_DEFAULT_VALUES.ILLUSTRATION'),
        highVolume: LocaleService.t('DISCLAIMERS_DEFAULT_VALUES.HIGH_VOLUME'),
        optionsVariation: LocaleService.t('DISCLAIMERS_DEFAULT_VALUES.OPTIONS_VARIATION'),
        dataResponsibility: '',
        priceChange: LocaleService.t('DISCLAIMERS_DEFAULT_VALUES.PRICE_CHANGE'),
        priceExclusion: LocaleService.t('DISCLAIMERS_DEFAULT_VALUES.PRICE_EXCLUSION'),
        taxesExcluded: LocaleService.t('DISCLAIMERS_DEFAULT_VALUES.TAXES_EXCLUDED', { taxCodes: ['Taxes'] }),
        pricePaymentsTaxesExcluded: LocaleService.t('DISCLAIMERS_DEFAULT_VALUES.PRICE_PAYMENTS_TAXES_EXCLUDED'),
    };
    if (isEmpty(genericDisclaimers)) {
        return new DisclaimersModel(sentences);
    }

    const {
        ILLUSTRATION_PURPOSES,
        HIGH_TRANSACTION_VOLUME,
        OPTIONS_TRIMS_VARIATION,
        NOT_RESPONSIBLE,
        PRICES_SUBJECT_TO_CHANGE,
        GENERAL_FEES_EXCLUSION,
        EXCLUDED_TAXES,
        PRICE_PAYMENTS_EXCLUDED_TAXES,
    } = DISCLAIMERS;

    const illustration = find(genericDisclaimers, (disclaimer) => disclaimer.id === ILLUSTRATION_PURPOSES);
    if (illustration) {
        sentences.illustration = illustration.disclaimer;
    }

    const highVolume = find(genericDisclaimers, (disclaimer) => disclaimer.id === HIGH_TRANSACTION_VOLUME);
    if (highVolume) {
        sentences.highVolume = highVolume.disclaimer;
    }

    const optionsVariation = find(genericDisclaimers, (disclaimer) => disclaimer.id === OPTIONS_TRIMS_VARIATION);
    if (optionsVariation) {
        sentences.optionsVariation = optionsVariation.disclaimer;
    }

    const dataResponsibility = find(genericDisclaimers, (disclaimer) => disclaimer.id === NOT_RESPONSIBLE);
    if (dataResponsibility) {
        sentences.dataResponsibility = dataResponsibility.disclaimer;
    }

    const priceChange = find(genericDisclaimers, (disclaimer) => disclaimer.id === PRICES_SUBJECT_TO_CHANGE);
    sentences.priceChange = priceChange ? priceChange.disclaimer : '';

    const priceExclusions = find(genericDisclaimers, (disclaimer) => disclaimer.id === GENERAL_FEES_EXCLUSION);
    if (priceExclusions) {
        sentences.priceExclusion = priceExclusions.disclaimer;
    }

    const taxesExcluded = find(genericDisclaimers, (disclaimer) => disclaimer.id === EXCLUDED_TAXES);
    if (taxesExcluded) {
        sentences.taxesExcluded = taxesExcluded.disclaimer;
    }

    const pricePaymentsTaxesExcluded = find(
        genericDisclaimers,
        (disclaimer) => disclaimer.id === PRICE_PAYMENTS_EXCLUDED_TAXES
    );
    if (pricePaymentsTaxesExcluded) {
        sentences.pricePaymentsTaxesExcluded = pricePaymentsTaxesExcluded.disclaimer;
    }

    return new DisclaimersModel(sentences);
}

/**
 * Util to merge Disclaimers API call response and target vehicles and return the new vehicles array
 * @param {Object} disclaimersData
 * @param {Object[]} vehiclesAndRequestData
 */
function mergeVehiclesAndDisclaimersDataResults(disclaimersData, vehiclesAndRequestData) {
    if (isEmpty(disclaimersData) || isEmpty(disclaimersData.disclaimers)) {
        return map(vehiclesAndRequestData, (vehicleAndRequestData) => vehicleAndRequestData.vehicle);
    }

    const { disclaimers } = disclaimersData;

    const baseDisclaimers = vehiclesAndRequestData[0].vehicle.purchaseMethods.disclaimers;
    const vehicleDisclaimers = map(disclaimers, (disclaimer) =>
        createDisclaimersSentences(disclaimer, baseDisclaimers.priceExclusion)
    );

    const vehiclesAndDisclaimers = map(vehiclesAndRequestData, (vehicleAndRequestData) => {
        const { cookie, vehicle } = vehicleAndRequestData;

        const vehicleAndDisclaimer = find(
            vehicleDisclaimers,
            (vehicleDisclaimer) => vehicleDisclaimer.cookie === cookie
        );

        if (!vehicleAndDisclaimer) {
            return vehicle.setIn(['purchaseMethods', 'disclaimers'], baseDisclaimers);
        }

        const { purchaseMethods } = vehicle;
        const { lease, finance } = vehicleAndDisclaimer;

        const financeSentences = getFinanceSentences(purchaseMethods.finance, finance);
        const leaseSentences = getFinanceSentences(purchaseMethods.lease, lease);

        const purchaseMethodsDisclaimers = baseDisclaimers
            .set('cashPurchase', vehicleAndDisclaimer.cashPurchase)
            .set('finance', financeSentences)
            .set('lease', leaseSentences);

        return vehicle.setIn(['purchaseMethods', 'disclaimers'], purchaseMethodsDisclaimers);
    });

    return vehiclesAndDisclaimers;
}

/**
 * Util to merge Disclaimers API call response and target options package and return the new model year
 * @param {Object} disclaimersData
 * @param {Object[]} optionsPackageAndRequestData
 */
function mergeOptionsPackageAndDisclaimersDataResults(disclaimersData, optionsPackageAndRequestData) {
    if (isEmpty(disclaimersData) || isEmpty(disclaimersData.disclaimers)) {
        return optionsPackageAndRequestData.modelYear;
    }

    const { disclaimers, genericDisclaimers } = disclaimersData;

    const baseDisclaimers = getBasicDisclaimers(genericDisclaimers);
    const vehicleDisclaimers = map(disclaimers, (disclaimer) =>
        createDisclaimersSentences(disclaimer, baseDisclaimers.priceExclusion)
    );

    const { cookie, modelYear, optionsPackage } = optionsPackageAndRequestData;

    const vehicleAndDisclaimer = find(vehicleDisclaimers, (vehicleDisclaimer) => vehicleDisclaimer.cookie === cookie);

    if (!vehicleAndDisclaimer) {
        return modelYear;
    }

    const { modelTrimCodeId, purchaseMethods } = optionsPackage.configuration;
    const { lease, finance } = vehicleAndDisclaimer;

    const financeSentences = getFinanceSentences(purchaseMethods.finance, finance);
    const leaseSentences = getFinanceSentences(purchaseMethods.lease, lease);

    const purchaseMethodsDisclaimers = baseDisclaimers
        .set('cashPurchase', vehicleAndDisclaimer.cashPurchase)
        .set('finance', financeSentences)
        .set('lease', leaseSentences);

    const newOptionsPackage = optionsPackage.setIn(
        ['configuration', 'purchaseMethods', 'disclaimers'],
        purchaseMethodsDisclaimers
    );

    return modelYear.set(
        'optionsPackages',
        List(
            map(modelYear.optionsPackages.toArray(), (modelOptionsPackage) => {
                const currentModelTrimCodeId = modelOptionsPackage.configuration.modelTrimCodeId;
                return currentModelTrimCodeId === modelTrimCodeId ? newOptionsPackage : modelOptionsPackage;
            })
        )
    );
}

/**
 * Util to merge Disclaimers API call response and target model and return the model and purchase methods
 * @param {Object} disclaimersData
 * @param {Object[]} modelsAndRequestData
 */
function mergeModelAndDisclaimersDataResults(disclaimersData, modelsAndRequestData) {
    const { CASH, FINANCE, LEASE } = PURCHASE_METHODS;
    const priceModelsAndRequestData = filter(
        modelsAndRequestData,
        (modelAndRequestPurchaseMethodData) => modelAndRequestPurchaseMethodData.purchaseMethod === CASH
    );

    if (isEmpty(disclaimersData) || isEmpty(disclaimersData.disclaimers)) {
        return map(priceModelsAndRequestData, (priceModelAndRequestData) => priceModelAndRequestData.model);
    }

    const { disclaimers, genericDisclaimers } = disclaimersData;

    const baseDisclaimers = getBasicDisclaimers(genericDisclaimers);
    const vehicleDisclaimers = map(disclaimers, (disclaimer) =>
        createDisclaimersSentences(disclaimer, baseDisclaimers.priceExclusion)
    );

    const priceModelsAndDisclaimers = map(priceModelsAndRequestData, (priceModelAndRequestData) => {
        const { model } = priceModelAndRequestData;

        const leasePurchaseMethods = find(
            modelsAndRequestData,
            (modelAndRequestData) =>
                modelAndRequestData.purchaseMethod === LEASE &&
                modelAndRequestData.model.make === model.make &&
                modelAndRequestData.model.model === model.model &&
                modelAndRequestData.model.bodyStyleCode === model.bodyStyleCode
        );
        const financePurchaseMethods = find(
            modelsAndRequestData,
            (modelAndRequestData) =>
                modelAndRequestData.purchaseMethod === FINANCE &&
                modelAndRequestData.model.make === model.make &&
                modelAndRequestData.model.model === model.model &&
                modelAndRequestData.model.bodyStyleCode === model.bodyStyleCode
        );

        const priceVehicleAndDisclaimer = find(
            vehicleDisclaimers,
            (vehicleDisclaimer) => vehicleDisclaimer.cookie === priceModelAndRequestData.cookie
        );
        const leaseVehicleAndDisclaimer = find(
            vehicleDisclaimers,
            (vehicleDisclaimer) => vehicleDisclaimer.cookie === leasePurchaseMethods.cookie
        );
        const financeVehicleAndDisclaimer = find(
            vehicleDisclaimers,
            (vehicleDisclaimer) => vehicleDisclaimer.cookie === financePurchaseMethods.cookie
        );

        if (!priceVehicleAndDisclaimer && !leaseVehicleAndDisclaimer && !financeVehicleAndDisclaimer) {
            return priceModelAndRequestData.model;
        }

        const { lease } = leaseVehicleAndDisclaimer;
        const { finance } = financeVehicleAndDisclaimer;

        const financeSentences = getFinanceSentences(financePurchaseMethods.purchaseMethods.finance, finance);
        const leaseSentences = getFinanceSentences(leasePurchaseMethods.purchaseMethods.lease, lease);

        const purchaseMethodsDisclaimers = baseDisclaimers
            .set('cashPurchase', priceVehicleAndDisclaimer.cashPurchase)
            .set('finance', financeSentences)
            .set('lease', leaseSentences);

        return priceModelAndRequestData.model.setIn(['purchaseMethods', 'disclaimers'], purchaseMethodsDisclaimers);
    });

    return priceModelsAndDisclaimers;
}

/**
 * Groups Disclaimer api calls to ShowroomAPI
 */
class DisclaimerAPIService {
    /**
     * Gets the generic disclaimers for a context
     */
    getGenericDisclaimers(config) {
        return ShowroomAPIService.getPublicToken().then((publicToken) => {
            const disclaimersRequestConfig = getGenericDisclaimersRequestConfig(publicToken.data, config);

            return axios
                .request(disclaimersRequestConfig)
                .then((result) => getBasicDisclaimers(result.data))
                .catch(() => {
                    return Promise.reject(HTTPService.formatRejectResponse('Unable to load disclaimers'));
                });
        });
    }

    /**
     * Gets the disclaimers for a list of vehicles
     * @param {Object[]} vehicles
     */
    getVehiclesDisclaimers(vehicles) {
        if (isEmpty(vehicles) || !isArrayLikeObject(vehicles) || !(vehicles[0] instanceof VehicleModel)) {
            return Promise.reject(HTTPService.formatRejectResponse('param [vehicles] must be an array'));
        }

        const vehiclesAndRequestData = getVehiclesDisclaimersDataRequestConfig(vehicles);

        return ShowroomAPIService.getPublicToken().then((publicToken) => {
            const disclaimersRequestConfig = getDisclaimersRequestConfig(publicToken.data, vehiclesAndRequestData);

            return axios
                .request(disclaimersRequestConfig)
                .then((result) => mergeVehiclesAndDisclaimersDataResults(result.data, vehiclesAndRequestData))
                .catch(() => {
                    return Promise.reject(HTTPService.formatRejectResponse('Unable to load disclaimers'));
                });
        });
    }

    /**
     * Gets the disclaimers for the selected options package of a Model Year
     * @param {Object} modelYear
     * @param {Object} selectedOptionsPackage
     */
    getModelYearSelectedOptionsPackageDisclaimers(modelYear, selectedOptionsPackage, configurationToken) {
        if (isEmpty(modelYear) || !(modelYear instanceof ModelYearModel) || isUndefined(selectedOptionsPackage)) {
            return Promise.reject(HTTPService.formatRejectResponse('param modelYear must be a valid Model Year'));
        }

        const optionsPackageAndRequestData = getOptionsPackageDisclaimersDataRequestConfig(
            modelYear,
            selectedOptionsPackage
        );

        optionsPackageAndRequestData[0].disclaimerData.configurationToken = configurationToken || '';

        return ShowroomAPIService.getPublicToken().then((publicToken) => {
            const disclaimersRequestConfig = getDisclaimersRequestConfig(
                publicToken.data,
                optionsPackageAndRequestData
            );

            return axios
                .request(disclaimersRequestConfig)
                .then((result) =>
                    mergeOptionsPackageAndDisclaimersDataResults(result.data, optionsPackageAndRequestData[0])
                )
                .catch(() => {
                    return Promise.reject(HTTPService.formatRejectResponse('Unable to load disclaimers'));
                });
        });
    }

    /**
     * Gets the disclaimers for a list of models
     * @param {Object[]} models
     */
    getModelsDisclaimers(models) {
        if (isEmpty(models) || !isArrayLikeObject(models)) {
            return Promise.reject(HTTPService.formatRejectResponse('param models must be valid'));
        }

        const modelsAndRequestData = getModelsDisclaimersDataRequestConfig(models);

        return ShowroomAPIService.getPublicToken().then((publicToken) => {
            const disclaimersRequestConfig = getDisclaimersRequestConfig(publicToken.data, modelsAndRequestData);
            return axios
                .request(disclaimersRequestConfig)
                .then((result) => mergeModelAndDisclaimersDataResults(result.data, modelsAndRequestData))
                .catch(() => {
                    return Promise.reject(HTTPService.formatRejectResponse('Unable to load disclaimers'));
                });
        });
    }
}

export { DisclaimerAPIService };
export default new DisclaimerAPIService();
